import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cookie helpers
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
};

// Get or create anonymous ID
export const getAnonymousId = () => {
  let anonId = getCookie('jigsaw_anon_id');
  if (!anonId) {
    anonId = `anon_${Math.random().toString(36).substr(2, 12)}`;
    setCookie('jigsaw_anon_id', anonId);
  }
  return anonId;
};

/**
 * Hook for tracking page views and user behavior
 * @param {Object} options - Tracking options
 * @param {string} options.eventId - Event ID if on event page
 * @param {string} options.eventSlug - Event slug
 * @param {string[]} options.sectorTags - Sector tags
 * @param {string} options.organiserName - Organiser name
 * @param {string} options.citySlug - City slug
 * @param {string} options.venueSlug - Venue slug
 * @param {string} options.pageType - Type of page (event_detail, city, sector, homepage)
 */
export const useTracking = (options = {}) => {
  const startTime = useRef(null);
  const tracked = useRef(false);
  
  // Initialize start time on first render
  if (startTime.current === null) {
    startTime.current = Date.now();
  }
  
  const trackPageView = useCallback(async (timeOnPage = 0) => {
    try {
      const anonId = getAnonymousId();
      
      await axios.post(
        `${BACKEND_URL}/api/tracking/pageview`,
        {
          eventId: options.eventId,
          eventSlug: options.eventSlug,
          sectorTags: options.sectorTags || [],
          organiserName: options.organiserName,
          citySlug: options.citySlug,
          venueSlug: options.venueSlug,
          timeOnPage,
          pageType: options.pageType || 'event_detail'
        },
        {
          headers: {
            'X-Anonymous-ID': anonId
          }
        }
      );
      
      // Also track in GA if available
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'page_view_tracked', {
          event_id: options.eventId,
          event_slug: options.eventSlug,
          city: options.citySlug,
          venue: options.venueSlug,
          sectors: options.sectorTags?.join(','),
          time_on_page: timeOnPage
        });
      }
      
    } catch (error) {
      // Silently fail - tracking is non-critical
      console.debug('Tracking failed:', error.message);
    }
  }, [options]);
  
  // Track initial page view
  useEffect(() => {
    if (!tracked.current && (options.eventId || options.citySlug || options.pageType)) {
      trackPageView(0);
      tracked.current = true;
    }
  }, [options.eventId, options.citySlug, options.pageType, trackPageView]);
  
  // Track time on page when leaving
  useEffect(() => {
    const handleBeforeUnload = () => {
      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      if (timeOnPage > 5) { // Only track if user spent > 5 seconds
        // Use sendBeacon for reliability on page unload
        const anonId = getAnonymousId();
        const data = JSON.stringify({
          eventId: options.eventId,
          eventSlug: options.eventSlug,
          sectorTags: options.sectorTags || [],
          organiserName: options.organiserName,
          citySlug: options.citySlug,
          venueSlug: options.venueSlug,
          timeOnPage,
          pageType: options.pageType || 'event_detail'
        });
        
        // Try to use sendBeacon if available
        if (navigator.sendBeacon) {
          const blob = new Blob([data], { type: 'application/json' });
          navigator.sendBeacon(`${BACKEND_URL}/api/tracking/pageview?anonId=${anonId}`, blob);
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [options, trackPageView]);
  
  return { trackPageView, getAnonymousId };
};

/**
 * Track recommendation click
 */
export const trackRecommendationClick = (recommendation, source = 'event_detail') => {
  try {
    // Track in GA
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'recommendation_click', {
        event_name: recommendation.eventName,
        event_id: recommendation.eventId,
        source,
        score: recommendation.score,
        reason: recommendation.reason
      });
    }
  } catch (error) {
    console.debug('Recommendation tracking failed:', error.message);
  }
};

export default useTracking;
